import React from "react";
import { Link } from "react-router-dom";
import { FiAlertTriangle, FiEdit2, FiUploadCloud, FiSearch } from "react-icons/fi";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import { isPending, type DeploymentPageAnalysis, type PageOutOfSyncItem } from "./deploymentUtils";

/**
 * Results of the on-demand "Analyze links" scan for one target. Every scanned page shows its
 * warnings/errors; the Deploy and "View changes" actions only appear on pages that are actually
 * pending (out of sync) : an already-deployed page with issues is a fix-in-editor situation, not
 * a deploy one. Error-severity issues block production deployment, hence the closing notice.
 */
const AnalysisList: React.FC<{
	pageAnalyses: DeploymentPageAnalysis[];
	analyzing: boolean;
	analyzed: boolean;
	hasErrors: boolean;
	targetLabel: string;
	/** Pages that are pending (out of sync). View changes / Revert only shown for these. */
	pendingItems: PageOutOfSyncItem[];
	onViewChanges?: (item: DeploymentPageAnalysis) => void;
	/** Deploy this page to preview only (not shown in production). Shown only for pending rows. */
	onDeploySingle?: (item: DeploymentPageAnalysis) => void;
	deployingSlugLanguage?: string | null;
	/** When true, single-page Deploy buttons are disabled (e.g. other panel is deploying). */
	disableDeploySingle?: boolean;
}> = ({ pageAnalyses, analyzing, analyzed, hasErrors, targetLabel, pendingItems, onViewChanges, onDeploySingle, deployingSlugLanguage, disableDeploySingle }) => (
	<div className="space-y-3">
		{analyzing && <p className="text-sm text-slate-500">Analyzing all pages (including already deployed)…</p>}
		{!analyzing && pageAnalyses.length === 0 && (
			<p className="text-sm text-slate-500">
				{analyzed
					? `No analysis issues for ${targetLabel}.`
					: `Run "Analyze links" above to scan all pages (including already deployed to ${targetLabel}).`}
			</p>
		)}
		{!analyzing && pageAnalyses.length > 0 && (
			<div className="space-y-4">
				{pageAnalyses.map((pa) => {
					const pagePath = pa.id
						? `/page/${encodeURIComponent(pa.id)}`
						: `/page/${encodeURIComponent(pa.language)}/${encodeURIComponent((pa.slug || "").trim())}`;
					return (
					<div
						key={`${pa.slug}-${pa.language}-${pa.id ?? "noid"}`}
						className="rounded-xl border border-slate-200 bg-white overflow-hidden"
					>
						<div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80 flex items-start justify-between gap-2">
							<div className="min-w-0">
								<p className="font-semibold text-slate-900">
									{pa.title || pa.slug}
									{pa.title && pa.slug !== pa.title && (
										<span className="ml-2 text-slate-500 font-normal text-xs">/{pa.slug}</span>
									)}
								</p>
								<p className="text-xs text-slate-500">
									{pa.slug} · {pa.language}
								</p>
							</div>
						<div className="shrink-0 flex items-center gap-1.5">
							{isPending(pa, pendingItems) && onDeploySingle && (
								<button
									type="button"
									onClick={() => onDeploySingle(pa)}
									disabled={disableDeploySingle || deployingSlugLanguage === `${pa.slug}|${pa.language}`}
									className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
									title="Deploy this page to preview"
								>
									{deployingSlugLanguage === `${pa.slug}|${pa.language}` ? (
										<LoadingSpinner size="sm" inline />
									) : (
										<FiUploadCloud className="h-3 w-3" />
									)}
									Deploy
								</button>
							)}
							{isPending(pa, pendingItems) && onViewChanges && (
								<button
									type="button"
									onClick={() => onViewChanges(pa)}
									className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
									title="View changes (diff)"
								>
									<FiSearch className="h-3 w-3" /> View changes
								</button>
							)}
							<Link
								to={pagePath}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
							>
								<FiEdit2 className="h-3.5 w-3.5" /> Open page
							</Link>
						</div>
						</div>
						<div className="px-3 py-2 space-y-1">
							{pa.issues.length === 0 ? (
								<p className="text-sm text-emerald-600">OK</p>
							) : (
								pa.issues.map((i, k) => (
									<div
										key={k}
										className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm ${
											i.severity === "error"
												? "bg-rose-50 text-rose-800"
												: "bg-amber-50 text-amber-800"
										}`}
									>
										<FiAlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
										<span>{i.message}</span>
									</div>
								))
							)}
						</div>
					</div>
					);
				})}
			</div>
		)}
		{analyzed && hasErrors && (
			<p className="text-sm font-medium text-rose-600">Errors block production deployment. Fix red issues first.</p>
		)}
	</div>
);

export default AnalysisList;
